import type { Game, Prediction } from '../../types'

/** Which of the dock's three states the entry is in. */
export type EntryDockKind = 'togo' | 'in' | 'locked'

export interface EntryDockModel {
  kind: EntryDockKind
  /** Unpredicted fixtures in kickoff order. Empty unless `kind` is 'togo'. */
  remaining: Game[]
  /** How the predicted scorelines split across home win / draw / away win. */
  split: { home: number; draw: number; away: number }
}

export interface EntryDockInput {
  games: Game[]
  predictions: Record<string, Prediction>
  deadlinePassed: boolean
  /** Any fixture in play or already settled — the week is running. */
  fixtureInPlay: boolean
  /** True while the start CTA's walk through every fixture is open. */
  isCycling: boolean
  /** The week closed before this account existed. */
  joinedLate: boolean
}

function splitPredictions(
  games: Game[],
  predictions: Record<string, Prediction>
): EntryDockModel['split'] {
  const split = { home: 0, draw: 0, away: 0 }
  for (const game of games) {
    const prediction = predictions[game.id]
    if (!prediction) continue
    if (prediction.homeScore > prediction.awayScore) split.home++
    else if (prediction.homeScore < prediction.awayScore) split.away++
    else split.draw++
  }
  return split
}

/**
 * What the dock at the foot of the grid should say, or `null` for nothing at all.
 *
 * The order of the four silent cases matters. `isCycling` in particular is not an
 * aesthetic choice: pressing the start CTA hands the player straight into the picker sheet, and a
 * dock that renders behind it is painting an entrance nobody is watching — then sits there stale
 * while they work through the fixtures. It appears when they leave the cycle, whichever way
 * they leave it.
 */
export function buildEntryDock({
  games,
  predictions,
  deadlinePassed,
  fixtureInPlay,
  isCycling,
  joinedLate,
}: EntryDockInput): EntryDockModel | null {
  if (games.length === 0 || joinedLate) return null
  if (isCycling) return null
  if (fixtureInPlay) return null

  const predictedCount = games.filter((game) => predictions[game.id]).length
  // The start CTA owns a gameweek nobody has touched.
  if (predictedCount === 0) return null

  const split = splitPredictions(games, predictions)
  if (deadlinePassed) return { kind: 'locked', remaining: [], split }
  if (predictedCount === games.length) return { kind: 'in', remaining: [], split }

  const remaining = games
    .filter((game) => !predictions[game.id])
    .sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime())
  return { kind: 'togo', remaining, split }
}
